import { fetchWebPlaybackToken, spotifyConnectAddToQueue, spotifyConnectNext, spotifyConnectPause, spotifyConnectPlay, spotifyConnectSeek, spotifyConnectTransfer, spotifyConnectVolume } from '../installed/music';

export interface LocalPlayerTrack {
  uri: string | null;
  name: string | null;
  artist: string | null;
  album: string | null;
  imageUrl: string | null;
  durationMs: number;
}

export interface LocalPlayerState {
  ready: boolean;
  deviceId: string | null;
  active: boolean;
  paused: boolean;
  positionMs: number;
  durationMs: number;
  track: LocalPlayerTrack;
  volume: number;
  error: string | null;
  needsReauth: boolean;
  needsPremium: boolean;
}

type Listener = (state: LocalPlayerState) => void;

declare global {
  interface Window {
    onSpotifyWebPlaybackSDKReady?: () => void;
    Spotify?: {
      Player: new (opts: {
        name: string;
        getOAuthToken: (cb: (token: string) => void) => void;
        volume?: number;
      }) => SpotifyPlayerInstance;
    };
  }
}

interface SpotifyPlayerInstance {
  connect: () => Promise<boolean>;
  disconnect: () => void;
  addListener: (event: string, cb: (payload: any) => void) => void;
  removeListener: (event: string, cb?: (payload: any) => void) => void;
  getCurrentState: () => Promise<any>;
  setVolume: (v: number) => Promise<void>;
  pause: () => Promise<void>;
  resume: () => Promise<void>;
  seek: (ms: number) => Promise<void>;
  nextTrack: () => Promise<void>;
  activateElement?: () => Promise<void>;
}

/** Cuántos temas siguientes sembrar en Connect al dar play. */
const FOLLOW_SEED_COUNT = 2;
/** Pedir más cola cuando falten estos ms para el final. */
const PREFETCH_BEFORE_END_MS = 12_000;
/** Armar fin de tema (el SDK a menudo pausa con position≈0, no cerca del duration). */
const ARM_END_BEFORE_MS = 4_000;

const emptyTrack = (): LocalPlayerTrack => ({
  uri: null,
  name: null,
  artist: null,
  album: null,
  imageUrl: null,
  durationMs: 0,
});

function initialState(): LocalPlayerState {
  return {
    ready: false,
    deviceId: null,
    active: false,
    paused: true,
    positionMs: 0,
    durationMs: 0,
    track: emptyTrack(),
    volume: 0.8,
    error: null,
    needsReauth: false,
    needsPremium: false,
  };
}

let sdkLoadPromise: Promise<void> | null = null;
let initPromise: Promise<LocalPlayerState> | null = null;
let player: SpotifyPlayerInstance | null = null;
let state = initialState();
const listeners = new Set<Listener>();
let onTrackEnded: (() => void) | null = null;
let onTrackUriChanged: ((uri: string) => void) | null = null;
let onNeedFollowPrefetch: (() => void) | null = null;
let lastUri: string | null = null;
let wasPaused = true;
/** Tras vaciar cola: ignorar player_state_changed hasta el próximo play. */
let suppressTrackUntilPlay = false;
/** URIs ya sembrados detrás del tema actual (play multi-uri o add-to-queue). */
let seededFollowUris: string[] = [];
let prefetchHookedForUri: string | null = null;
let forceNextHookedForUri: string | null = null;
/** URI armado porque vimos position cerca del final mientras sonaba. */
let armedEndUri: string | null = null;
/** Timer propio: el SDK a menudo no emite pause-al-final (móvil/desktop). */
let endWatchTimer: ReturnType<typeof setTimeout> | null = null;
let endWatchUri: string | null = null;
let endWatchRunning = false;
/** Quién registró los callbacks (evita que otro mount los borre). */
let localCallbacksOwner: string | null = null;

const READY_TIMEOUT_MS = 10000;
const TRANSFER_RETRY_DELAYS_MS = [350, 700, 1200];

function clearEndWatch() {
  if (endWatchTimer != null) {
    clearTimeout(endWatchTimer);
    endWatchTimer = null;
  }
  endWatchUri = null;
}

function fireTrackEnded(uri: string | null) {
  const key = uri || lastUri || '__end__';
  if (forceNextHookedForUri === key) return;
  forceNextHookedForUri = key;
  armedEndUri = null;
  clearEndWatch();
  onTrackEnded?.();
}

/**
 * Programa avance por tiempo restante. No depende de que el SDK mande
 * player_state_changed al terminar (falla frecuente en Web Playback).
 */
function scheduleEndWatch(uri: string, durationMs: number, positionMs: number, paused: boolean) {
  clearEndWatch();
  if (!uri || durationMs <= 0 || paused || suppressTrackUntilPlay) return;
  const remaining = durationMs - positionMs;
  // Un poco después del final teórico; si Connect avanzó solo, el URI ya cambió.
  const delay = Math.max(100, remaining + 180);
  endWatchUri = uri;
  endWatchTimer = setTimeout(() => {
    endWatchTimer = null;
    void (async () => {
      if (endWatchRunning || suppressTrackUntilPlay) return;
      if (endWatchUri !== uri) return;
      endWatchRunning = true;
      try {
        let sdk: any = null;
        try {
          sdk = await player?.getCurrentState();
        } catch {
          /* ignore */
        }
        const s = getLocalPlayerState();
        const curUri = sdk?.track_window?.current_track?.uri ?? s.track.uri;
        // Connect ya pasó a otro tema: no disparar ended del anterior.
        if (curUri && curUri !== uri) return;
        const sdkPaused = sdk ? Boolean(sdk.paused) : s.paused;
        const sdkPos = sdk ? (Number(sdk.position) || 0) : s.positionMs;
        const sdkDur = Number(sdk?.track_window?.current_track?.duration_ms) || s.durationMs || durationMs;
        const atOrPastEnd = sdkDur > 0 && sdkPos >= Math.max(0, sdkDur - 800);
        // Sin state del SDK (= idle) o pausado / al final → avanzar cola SHC.
        if (!sdk || sdkPaused || atOrPastEnd) {
          fireTrackEnded(uri);
          return;
        }
        // Sigue el mismo tema (seek / drift): reprogramar.
        scheduleEndWatch(uri, sdkDur, sdkPos, false);
      } finally {
        endWatchRunning = false;
      }
    })();
  }, delay);
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => { window.setTimeout(resolve, ms); });
}

/** Clasifica errores de reproducción local para snackbars (sin falsos positivos de reauth). */
export function localPlaybackErrorKind(err: unknown): 'premium' | 'reauth' | 'other' {
  const s = getLocalPlayerState();
  const detail = String(
    (err as { response?: { data?: { detail?: string } }; message?: string })?.response?.data?.detail
    || (err as { message?: string })?.message
    || '',
  );
  if (detail === 'premium_required' || s.needsPremium || /\bpremium\b/i.test(detail)) {
    return 'premium';
  }
  // Reauth solo con evidencia del error actual. NO usar needsReauth pegajoso si el
  // Web Player ya quedó ready: authentication_error del SDK suele dispararse una
  // vez al conectar y después el dispositivo funciona (2º intento “magia”).
  if (
    detail === 'reauth_required'
    || /permisos faltantes|has_streaming/i.test(detail)
    || /volv[eé] a vincular/i.test(detail)
    || /scopes?\s+(streaming\s+)?falt/i.test(detail)
  ) {
    // Backend mapea varios 401/403 de Connect a “volvé a vincular”; si el SDK
    // ya está ready, no es un problema de vinculación.
    if (s.ready && s.deviceId) return 'other';
    return 'reauth';
  }
  if (s.needsReauth && !s.ready) {
    return 'reauth';
  }
  return 'other';
}

function waitForReady(timeoutMs = READY_TIMEOUT_MS): Promise<LocalPlayerState> {
  if (state.ready && state.deviceId) return Promise.resolve(state);
  // Premium sí es terminal; needsReauth/authentication_error del SDK NO — a menudo
  // llegan antes del evento `ready` y el player termina OK.
  if (state.needsPremium) {
    return Promise.reject(new Error(state.error || 'premium_required'));
  }

  return new Promise((resolve, reject) => {
    let settled = false;
    let unsub: (() => void) | undefined;

    const settle = (action: () => void) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timer);
      unsub?.();
      action();
    };

    const timer = window.setTimeout(() => {
      const err = state.needsPremium
        ? 'premium_required'
        : (state.needsReauth
          || state.error === 'authentication_error'
          || state.error === 'missing_streaming_scope')
          ? 'reauth_required'
          : (state.error || 'sdk_not_ready');
      if (err === 'reauth_required') {
        setState({ needsReauth: true, error: 'reauth_required' });
      }
      settle(() => reject(new Error(err)));
    }, timeoutMs);

    const onState = (s: LocalPlayerState) => {
      if (s.ready && s.deviceId) {
        settle(() => resolve(s));
        return;
      }
      if (s.needsPremium) {
        settle(() => reject(new Error(s.error || 'premium_required')));
      }
      // No abortar por needsReauth aquí: esperar ready o timeout.
    };

    unsub = subscribeLocalPlayer(onState);
    if (settled) unsub();
  });
}

function isTransientTransferError(err: unknown): boolean {
  const status = (err as { response?: { status?: number } })?.response?.status;
  if (status === 404 || status === 502 || status === 503 || status === 504) return true;
  // Con el Web Player ready, un 403/401 mapeado suele ser “device aún no visible”
  // o un glitch breve de token — no scopes faltantes reales.
  if ((status === 403 || status === 401) && state.ready && state.deviceId && !state.needsPremium) {
    return true;
  }
  const detail = String(
    (err as { response?: { data?: { detail?: string } }; message?: string })?.response?.data?.detail
    || (err as { message?: string })?.message
    || '',
  );
  return /no hay un dispositivo|device.*not|sdk_not_ready|prob[aá] de nuevo/i.test(detail);
}

function emit() {
  listeners.forEach((l) => l(state));
}

function setState(patch: Partial<LocalPlayerState>) {
  state = { ...state, ...patch };
  emit();
}

export function getLocalPlayerState(): LocalPlayerState {
  return state;
}

export function subscribeLocalPlayer(listener: Listener): () => void {
  listeners.add(listener);
  listener(state);
  return () => { listeners.delete(listener); };
}

/**
 * Registra callbacks del Web Player local.
 * `owner` evita que otro componente (p. ej. modal Sonos) borre los del tab Audio.
 */
export function setOnLocalTrackEnded(cb: (() => void) | null, owner?: string) {
  if (cb) {
    localCallbacksOwner = owner ?? null;
    onTrackEnded = cb;
    return;
  }
  if (owner && localCallbacksOwner && owner !== localCallbacksOwner) return;
  onTrackEnded = null;
  if (!onTrackUriChanged && !onNeedFollowPrefetch) localCallbacksOwner = null;
}

/** Spotify avanzó solo al siguiente URI (cola Connect). */
export function setOnLocalTrackUriChanged(cb: ((uri: string) => void) | null, owner?: string) {
  if (cb) {
    localCallbacksOwner = owner ?? localCallbacksOwner;
    onTrackUriChanged = cb;
    return;
  }
  if (owner && localCallbacksOwner && owner !== localCallbacksOwner) return;
  onTrackUriChanged = null;
  if (!onTrackEnded && !onNeedFollowPrefetch) localCallbacksOwner = null;
}

/** Pedir más siguientes a encolar (cerca del final). */
export function setOnLocalNeedFollowPrefetch(cb: (() => void) | null, owner?: string) {
  if (cb) {
    localCallbacksOwner = owner ?? localCallbacksOwner;
    onNeedFollowPrefetch = cb;
    return;
  }
  if (owner && localCallbacksOwner && owner !== localCallbacksOwner) return;
  onNeedFollowPrefetch = null;
  if (!onTrackEnded && !onTrackUriChanged) localCallbacksOwner = null;
}

function loadSdk(): Promise<void> {
  if (window.Spotify?.Player) return Promise.resolve();
  if (sdkLoadPromise) return sdkLoadPromise;
  sdkLoadPromise = new Promise((resolve, reject) => {
    const prev = window.onSpotifyWebPlaybackSDKReady;
    window.onSpotifyWebPlaybackSDKReady = () => {
      try { prev?.(); } catch { /* ignore */ }
      resolve();
    };
    const existing = document.querySelector('script[data-spotify-sdk]');
    if (existing) return;
    const script = document.createElement('script');
    script.src = 'https://sdk.scdn.co/spotify-player.js';
    script.async = true;
    script.dataset.spotifySdk = '1';
    script.onerror = () => reject(new Error('No se pudo cargar Spotify Web Playback SDK'));
    document.body.appendChild(script);
    window.setTimeout(() => {
      if (window.Spotify?.Player) resolve();
    }, 8000);
  });
  return sdkLoadPromise;
}

async function createAndConnectPlayer(): Promise<LocalPlayerState> {
  await loadSdk();
  if (!window.Spotify?.Player) {
    setState({ error: 'sdk_not_ready', ready: false });
    return state;
  }

  if (player) {
    try { player.disconnect(); } catch { /* ignore */ }
    player = null;
  }

  // Reset transient flags; keep volume preference.
  setState({
    ready: false,
    deviceId: null,
    active: false,
    error: null,
    needsReauth: false,
    needsPremium: false,
  });

  const instance = new window.Spotify.Player({
    name: 'Smart Home Control',
    getOAuthToken: (cb) => {
      void fetchWebPlaybackToken()
        .then((tok) => {
          if (tok.is_premium === false) {
            setState({ needsPremium: true, error: 'premium_required' });
          }
          // has_streaming false es una pista, no abortar el connect: el token
          // puede igual servir y `ready` confirma auth real.
          if (tok.has_streaming === false && !state.ready) {
            setState({ error: 'missing_streaming_scope' });
          }
          cb(tok.access_token);
        })
        .catch((err: any) => {
          const status = err?.response?.status;
          const detail = String(err?.response?.data?.detail || '');
          if (status === 403 && /premium/i.test(detail)) {
            setState({ needsPremium: true, error: 'premium_required' });
          } else if (status === 403 && /permisos faltantes|volv[eé] a vincular|scopes?\s+falt/i.test(detail)) {
            setState({ needsReauth: true, error: 'reauth_required' });
          }
          setState({ error: detail || 'token_error' });
          cb('');
        });
    },
    volume: state.volume,
  });

  instance.addListener('ready', ({ device_id }: { device_id: string }) => {
    // Si el SDK llegó a ready, la autenticación funcionó (authentication_error
    // previo suele ser ruido al primer connect).
    setState({ ready: true, deviceId: device_id, error: null, needsReauth: false });
  });
  instance.addListener('not_ready', () => {
    setState({ ready: false, deviceId: null, active: false });
  });
  instance.addListener('authentication_error', () => {
    // No marcar needsReauth de inmediato: el SDK a menudo emite esto una vez
    // antes de completar getOAuthToken/ready. Solo queda como pista de timeout.
    if (!state.ready) {
      setState({ error: 'authentication_error' });
    }
  });
  instance.addListener('account_error', () => {
    setState({ needsPremium: true, error: 'premium_required' });
  });
  instance.addListener('initialization_error', () => {
    setState({ error: 'sdk_init_error', ready: false });
  });
  instance.addListener('playback_error', () => {
    setState({ error: 'playback_error' });
  });
  instance.addListener('player_state_changed', (playbackState: any) => {
    if (suppressTrackUntilPlay) {
      // Cola vaciada a propósito: no rehidratar el track desde el SDK.
      if (!playbackState) {
        setState({ active: false, paused: true });
      } else {
        setState({
          active: false,
          paused: true,
          positionMs: 0,
          durationMs: 0,
          track: emptyTrack(),
        });
      }
      return;
    }
    if (!playbackState) {
      // null es frecuente en transfer/reconnect: solo avanzar si ya estábamos
      // armados cerca del final (no a mitad del tema).
      if (armedEndUri) {
        wasPaused = true;
        setState({ active: false, paused: true });
        fireTrackEnded(armedEndUri);
        return;
      }
      setState({ active: false, paused: true });
      return;
    }
    const current = playbackState.track_window?.current_track;
    const artists = (current?.artists || []).map((a: any) => a.name).filter(Boolean).join(', ');
    const imageUrl = current?.album?.images?.[0]?.url ?? null;
    const uri = current?.uri ?? null;
    const paused = Boolean(playbackState.paused);
    const positionMs = Number(playbackState.position) || 0;
    const durationMs = Number(current?.duration_ms) || 0;

    // Cambio de URI: Connect avanzó solo → sincronizar cola SHC (no re-play).
    if (lastUri && uri && lastUri !== uri) {
      lastUri = uri;
      wasPaused = paused;
      prefetchHookedForUri = null;
      forceNextHookedForUri = null;
      armedEndUri = null;
      clearEndWatch();
      if (seededFollowUris[0] === uri) {
        seededFollowUris = seededFollowUris.slice(1);
      } else {
        seededFollowUris = [];
      }
      setState({
        active: true,
        paused,
        positionMs,
        durationMs,
        track: {
          uri,
          name: current?.name ?? null,
          artist: artists || null,
          album: current?.album?.name ?? null,
          imageUrl,
          durationMs,
        },
      });
      onTrackUriChanged?.(uri);
      if (!paused && durationMs > 0) {
        scheduleEndWatch(uri, durationMs, positionMs, false);
      }
      return;
    }

    // Precargar siguientes ~12s antes del final (best-effort).
    if (
      !paused
      && uri
      && durationMs > 0
      && positionMs >= Math.max(0, durationMs - PREFETCH_BEFORE_END_MS)
      && prefetchHookedForUri !== uri
    ) {
      prefetchHookedForUri = uri;
      onNeedFollowPrefetch?.();
    }

    // Armar fin mientras aún suena cerca del final.
    if (
      !paused
      && uri
      && durationMs > 0
      && positionMs >= Math.max(0, durationMs - ARM_END_BEFORE_MS)
    ) {
      armedEndUri = uri;
    }

    // Reloj propio + fallback por pause-al-final.
    if (uri && durationMs > 0) {
      scheduleEndWatch(uri, durationMs, positionMs, paused);
    } else if (paused) {
      clearEndWatch();
    }

    const pausedAfterNaturalEnd = (
      paused
      && !wasPaused
      && durationMs > 0
      && (
        (Boolean(uri) && (
          positionMs >= Math.max(0, durationMs - 2000)
          || (armedEndUri === uri && positionMs <= 1500)
        ))
        || (!uri && Boolean(armedEndUri || lastUri))
      )
    );
    if (pausedAfterNaturalEnd) {
      fireTrackEnded(uri || armedEndUri || lastUri);
    }

    lastUri = uri || lastUri;
    wasPaused = paused;
    setState({
      active: true,
      paused,
      positionMs,
      durationMs,
      track: {
        uri,
        name: current?.name ?? null,
        artist: artists || null,
        album: current?.album?.name ?? null,
        imageUrl,
        durationMs,
      },
    });
  });

  player = instance;
  const ok = await instance.connect();
  if (!ok) {
    setState({ error: 'sdk_connect_failed', ready: false });
    return state;
  }
  // connect() resuelve antes del evento `ready`; hay que esperar device_id.
  if (!state.ready || !state.deviceId) {
    try {
      await waitForReady(READY_TIMEOUT_MS);
    } catch {
      /* state.error ya seteado; devolvemos state para que ensure* lance */
    }
  }
  return state;
}

export async function initSpotifyWebPlayer(): Promise<LocalPlayerState> {
  if (player && state.ready && state.deviceId) return state;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    // Si ya hay player conectándose, esperar ready en lugar de destruirlo.
    if (player && !state.ready) {
      try {
        return await waitForReady(READY_TIMEOUT_MS);
      } catch {
        /* recrear abajo */
      }
    }
    return createAndConnectPlayer();
  })().finally(() => {
    initPromise = null;
  });

  return initPromise;
}

export async function ensureLocalPlayerReady(): Promise<LocalPlayerState> {
  const s = await initSpotifyWebPlayer();
  if (!s.ready || !s.deviceId) {
    throw new Error(s.error || 'sdk_not_ready');
  }
  try {
    await player?.activateElement?.();
  } catch {
    /* iOS gesture helper */
  }
  return s;
}

export async function transferToLocalDevice(play = false): Promise<void> {
  const s = await ensureLocalPlayerReady();
  let lastErr: unknown;
  for (let attempt = 0; attempt <= TRANSFER_RETRY_DELAYS_MS.length; attempt++) {
    try {
      await spotifyConnectTransfer(s.deviceId!, play);
      setState({ active: true, needsReauth: false, error: null });
      return;
    } catch (e) {
      lastErr = e;
      if (attempt >= TRANSFER_RETRY_DELAYS_MS.length || !isTransientTransferError(e)) {
        break;
      }
      await sleep(TRANSFER_RETRY_DELAYS_MS[attempt]);
    }
  }
  // El Web Player ya es un destino válido. Elegir "este dispositivo" no debe
  // fallar por un transfer Connect tardío/transitorio (típico del 1º intento).
  const ready = getLocalPlayerState();
  if (ready.ready && ready.deviceId && !ready.needsPremium) {
    setState({ active: true });
    return;
  }
  throw lastErr;
}

export type PlayLocalUriOptions = {
  positionMs?: number;
  /** Temas siguientes a sembrar en la cola Connect (gap más corto). */
  followingUris?: string[];
  /** Duración conocida (cola SHC) para armar el reloj de fin si el SDK tarda. */
  durationMs?: number;
};

/**
 * Reproduce un URI en el Web Player.
 * Si ya estamos en este dispositivo, evita re-transfer.
 * Si hay followingUris, los manda juntos en start_playback para que Spotify
 * avance solo al terminar (mucho menos silencio que play-por-HTTP al final).
 */
export async function playLocalUri(uri: string, positionMsOrOpts: number | PlayLocalUriOptions = 0): Promise<void> {
  const opts: PlayLocalUriOptions = typeof positionMsOrOpts === 'number'
    ? { positionMs: positionMsOrOpts }
    : (positionMsOrOpts || {});
  const positionMs = opts.positionMs ?? 0;
  const following = (opts.followingUris || []).filter((u) => u && u !== uri).slice(0, FOLLOW_SEED_COUNT);

  suppressTrackUntilPlay = false;
  prefetchHookedForUri = null;
  forceNextHookedForUri = null;
  armedEndUri = null;
  clearEndWatch();

  // Si Connect ya está en este URI (auto-advance), no reiniciar el tema.
  const current = getLocalPlayerState();
  if (current.active && current.track.uri === uri && !current.paused && positionMs === 0) {
    seededFollowUris = following;
    if (following.length) {
      void ensureLocalFollowingQueued(following);
    }
    return;
  }

  await ensureLocalPlayerReady();
  let s = getLocalPlayerState();
  // Transfer solo si aún no somos el destino activo.
  if (!s.deviceId || !s.active) {
    await transferToLocalDevice(false);
    s = getLocalPlayerState();
  }
  const uris = [uri, ...following];
  await spotifyConnectPlay({
    device_id: s.deviceId!,
    uris,
    position_ms: positionMs,
  });
  seededFollowUris = [...following];
  lastUri = uri;
  wasPaused = false;
  setState({ active: true, paused: false });
  // Reloj de fin con duración de la cola SHC (no esperar al SDK).
  const knownDur = Number(opts.durationMs) || 0;
  if (knownDur > 0) {
    scheduleEndWatch(uri, knownDur, positionMs, false);
  }
}

/** Encola URIs que aún no están sembrados detrás del tema actual. */
export async function ensureLocalFollowingQueued(uris: string[]): Promise<void> {
  const deviceId = getLocalPlayerState().deviceId;
  if (!deviceId) return;
  const wanted = uris.filter(Boolean);
  for (const uri of wanted) {
    if (seededFollowUris.includes(uri)) continue;
    try {
      await spotifyConnectAddToQueue(uri, deviceId);
      seededFollowUris.push(uri);
    } catch {
      /* best-effort: el advance por HTTP sigue siendo fallback */
    }
  }
}

export async function skipLocalToNext(): Promise<void> {
  const deviceId = getLocalPlayerState().deviceId;
  try {
    if (player) {
      await player.nextTrack();
      return;
    }
  } catch {
    /* fall through */
  }
  await spotifyConnectNext(deviceId || undefined);
}

export async function pauseLocalPlayback(): Promise<void> {
  const deviceId = state.deviceId;
  try {
    if (player) await player.pause();
  } catch {
    /* ignore */
  }
  // Solo llamar a Connect si hubo Web Player activo. Si el usuario solo eligió
  // un parlante Sonos, evitamos un 401 de Spotify (scopes) que el interceptor
  // global podía interpretar como JWT SHC expirado.
  if (state.active || deviceId) {
    try {
      await spotifyConnectPause(deviceId || undefined);
    } catch {
      /* ignore — destino Sonos / scopes faltantes no deben romper la sesión */
    }
  }
  setState({ paused: true });
}

/** Pausa y limpia el now-playing local (p. ej. al vaciar la cola SHC). */
export async function clearLocalPlayback(): Promise<void> {
  suppressTrackUntilPlay = true;
  lastUri = null;
  wasPaused = true;
  seededFollowUris = [];
  prefetchHookedForUri = null;
  forceNextHookedForUri = null;
  armedEndUri = null;
  clearEndWatch();
  try {
    await pauseLocalPlayback();
  } catch {
    /* ignore */
  }
  setState({
    active: false,
    paused: true,
    positionMs: 0,
    durationMs: 0,
    track: emptyTrack(),
  });
}

export async function resumeLocalPlayback(): Promise<void> {
  suppressTrackUntilPlay = false;
  const s = await ensureLocalPlayerReady();
  try {
    await player?.resume();
  } catch {
    await spotifyConnectPlay({ device_id: s.deviceId! });
  }
  setState({ paused: false, active: true });
}

export async function seekLocalPlayback(positionMs: number): Promise<void> {
  const s = state.deviceId ? state : await ensureLocalPlayerReady();
  try {
    await player?.seek(positionMs);
  } catch {
    await spotifyConnectSeek(positionMs, s.deviceId || undefined);
  }
  setState({ positionMs });
}

export async function setLocalVolume(volume01: number): Promise<void> {
  const v = Math.max(0, Math.min(1, volume01));
  setState({ volume: v });
  try {
    await player?.setVolume(v);
  } catch {
    await spotifyConnectVolume(Math.round(v * 100), state.deviceId || undefined);
  }
}

export function disconnectLocalPlayer(): void {
  try { player?.disconnect(); } catch { /* ignore */ }
  player = null;
  state = initialState();
  emit();
}
